import React from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Flex,
  Box,
  Heading,
  Text,
  Button,
  StatusIndicator,
  LoadingOrbit,
  Card,
  Page,
  DataTable,
  Dialog,
  DialogTrigger,
  DialogContent,
  TagBadge,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { CopyablePre } from '../components/CopyButton';
import {
  useKnownPageAnalysisJobDetails,
  type ChildJob,
} from '../../features/qa/useKnownPageAnalysis';

export const KnownPageAnalysisDetail: React.FC = () => {
  const { propertyId, jobId } = useParams({
    from: '/applications/$propertyId/known-page-analysis/$jobId',
  });
  const navigate = useNavigate();
  const jobDetailsQ = useKnownPageAnalysisJobDetails(jobId);

  // Fetch property name for breadcrumbs
  const propertyQ = useQuery({
    queryKey: ['property', propertyId],
    queryFn: async (): Promise<{ id: string; name: string } | null> => {
      const r = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
      if (!r.ok) return null;
      const data = await r.json();
      const property = data.items?.find((p: any) => p.id === propertyId);
      return property ? { id: property.id, name: property.name } : null;
    },
  });

  // Set breadcrumbs
  useBreadcrumbs(
    propertyQ.data && jobDetailsQ.data
      ? [
          {
            label: 'Applications',
            onPress: () => navigate({ to: '/' }),
          },
          {
            label: propertyQ.data.name,
            onPress: () => navigate({ to: '/applications/$propertyId', params: { propertyId } }),
          },
          {
            label: `Known Page Analysis ${new Date(jobDetailsQ.data.job.createdAt).toLocaleDateString()}`,
          },
        ]
      : []
  );

  const columns: ColumnDef<StockFeatures, ChildJob>[] = [
    {
      accessorKey: 'status',
      header: 'Job Status',
      cell: ({ row }) => {
        const status = row.original.status;
        if (status === 'running') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Running
            </span>
          );
        }
        if (status === 'queued') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Queued
            </span>
          );
        }
        if (status === 'completed') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Completed
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Failed
          </span>
        );
      },
      meta: { flex: 1 },
    },
    {
      accessorKey: 'qaReport.status',
      header: 'QA Status',
      cell: ({ row }) => {
        const qaReport = row.original.qaReport;
        if (!qaReport) {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              No Report
            </span>
          );
        }
        const status = qaReport.status;
        if (status === 'running') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Running
            </span>
          );
        }
        if (status === 'pending') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Pending
            </span>
          );
        }
        if (status === 'issues_found') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Issues Found ({qaReport.issuesFoundCount})
            </span>
          );
        }
        if (status === 'no_issues') {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              No Issues
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Failed
          </span>
        );
      },
      meta: { flex: 1.5 },
    },
    {
      accessorKey: 'qaReportId',
      header: 'QA Report',
      cell: ({ row }) => {
        const canonicalPageId = row.original.payload.canonicalPageId;
        return (
          <Button
            variant="link"
            size="sm"
            className="text-blue-600 hover:text-blue-700 underline decoration-1 underline-offset-2"
            onPress={() =>
              navigate({
                to: '/pages/$propertyId/canonical/$canonicalPageId',
                params: { propertyId, canonicalPageId },
              })
            }
          >
            View Page
          </Button>
        );
      },
      meta: { flex: 1.5 },
    },
    {
      accessorKey: 'createdAt',
      header: 'Started',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString()}
        </span>
      ),
      meta: { flex: 2 },
    },
    {
      accessorKey: 'completedAt',
      header: 'Completed',
      cell: ({ row }) =>
        row.original.completedAt ? (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.completedAt).toLocaleString()}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
      meta: { flex: 2 },
    },
    {
      accessorKey: 'lastError',
      header: 'Error',
      cell: ({ row }) => {
        const hasError = row.original.lastError || row.original.qaReport?.details;
        if (!hasError) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        return (
          <DialogTrigger size="2xl">
            <Button variant="link" size="sm" className="text-red-600 hover:text-red-700 underline">
              View Details
            </Button>
            <Dialog title="Error Details">
              <DialogContent>
                {row.original.lastError && (
                  <div className="mb-4">
                    <Text weight="semibold" className="mb-2">
                      Job Error:
                    </Text>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <Text size="sm" className="text-red-800 whitespace-pre-wrap">
                        {row.original.lastError}
                      </Text>
                    </div>
                  </div>
                )}
                {row.original.qaReport?.details && (
                  <div>
                    <Text weight="semibold" className="mb-2">
                      QA Report Diagnostics:
                    </Text>
                    {(() => {
                      const detailsText = JSON.stringify(row.original.qaReport.details, null, 2);
                      return (
                        <CopyablePre value={detailsText} label="diagnostics">
                          <div className="bg-muted p-3">
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                              {detailsText}
                            </pre>
                          </div>
                        </CopyablePre>
                      );
                    })()}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </DialogTrigger>
        );
      },
      meta: { flex: 1.5 },
    },
  ];

  if (jobDetailsQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (jobDetailsQ.isError || !jobDetailsQ.data) {
    return (
      <Box>
        <Text>Failed to load job details</Text>
        <Button
          onPress={() => navigate({ to: '/applications/$propertyId', params: { propertyId } })}
        >
          Back to Application
        </Button>
      </Box>
    );
  }

  const { job, childJobs, jobStats: _jobStats, qaStats } = jobDetailsQ.data;

  // Check if job is still running
  const isRunning = job.status === 'running' || job.status === 'queued';

  return (
    <Page>
      <Flex direction="column" gap="lg">
        {/* Header */}
        <div>
          <Flex align="center" justify="between">
            <div>
              <Heading level={2}>
                Known Page Analysis {new Date(job.createdAt).toLocaleDateString()}
              </Heading>
              <Flex gap="sm" className="mt-2" wrap>
                <TagBadge
                  name={new Date(job.createdAt).toLocaleString()}
                  color="gray"
                  hideDot
                  size="sm"
                />
              </Flex>
            </div>
            <Flex align="center" gap="md">
              {job.status === 'running' ? (
                <StatusIndicator variant="positive">Running</StatusIndicator>
              ) : job.status === 'queued' ? (
                <StatusIndicator variant="notice">Queued</StatusIndicator>
              ) : job.status === 'completed' ? (
                <StatusIndicator variant="neutral">Completed</StatusIndicator>
              ) : (
                <StatusIndicator variant="negative">Failed</StatusIndicator>
              )}
            </Flex>
          </Flex>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              Total Tests
            </Text>
            <Heading level={3} className="mt-1">
              {qaStats.total}
            </Heading>
          </Card>
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              Issues Found
            </Text>
            <Heading level={3} className="mt-1 text-red-600">
              {qaStats.issues_found}
            </Heading>
          </Card>
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              No Issues
            </Text>
            <Heading level={3} className="mt-1 text-green-600">
              {qaStats.no_issues}
            </Heading>
          </Card>
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              Running
            </Text>
            <Heading level={3} className="mt-1 text-blue-600">
              {qaStats.running}
            </Heading>
          </Card>
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              Pending
            </Text>
            <Heading level={3} className="mt-1 text-gray-600">
              {qaStats.pending}
            </Heading>
          </Card>
          <Card variant="elevated" padding="md">
            <Text size="sm" className="text-muted-foreground">
              Failed
            </Text>
            <Heading level={3} className="mt-1 text-red-600">
              {qaStats.failed}
            </Heading>
          </Card>
        </div>

        {/* Progress Bar */}
        {isRunning && (
          <Card variant="elevated" padding="md">
            <Flex direction="column" gap="sm">
              <Flex align="center" justify="between">
                <Text weight="semibold">Overall Progress</Text>
                <Text size="sm" className="text-muted-foreground">
                  {qaStats.issues_found + qaStats.no_issues + qaStats.failed} / {qaStats.total}
                </Text>
              </Flex>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${qaStats.total > 0 ? ((qaStats.issues_found + qaStats.no_issues + qaStats.failed) / qaStats.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </Flex>
          </Card>
        )}

        {/* Child Jobs Table */}
        <Card variant="elevated" padding="md">
          <Flex align="center" justify="between" className="mb-3">
            <Text weight="semibold">Test Jobs ({childJobs.length})</Text>
          </Flex>
          {childJobs.length > 0 ? (
            <DataTable columns={columns} data={childJobs} aria-label="Child jobs table" />
          ) : (
            <Text className="text-muted-foreground text-center py-8">No test jobs found</Text>
          )}
        </Card>
      </Flex>
    </Page>
  );
};
